import {
    CHIRON_FIRST_MS,
    CHIRON_LAST_MS,
    chironLongitude,
    chironRetrograde,
} from '../tarot/astro/chiron';
import { SignId, SIGNS, signOf } from '../tarot/astro/zodiac';

const DAY = 86_400_000;

export interface ChironFinding {
    sign: SignId;
    /** Degrees inside the sign. */
    degree: number;
    retrograde: boolean;
    /** The sign changed during that UTC day, so the date alone cannot tell. */
    edge: boolean;
}

/** Chiron on a calendar date (UTC noon), or null when the table does not reach it. */
export const chironOnDate = (iso: string): ChironFinding | null => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    const noon = Date.UTC(y, m - 1, d, 12);
    const start = noon - 12 * 3_600_000;
    const end = start + DAY - 1000;
    if (start < CHIRON_FIRST_MS || end > CHIRON_LAST_MS) return null;
    const lon = chironLongitude(noon);
    return {
        sign: signOf(lon),
        degree: lon % 30,
        retrograde: chironRetrograde(noon),
        edge: signOf(chironLongitude(start)) !== signOf(chironLongitude(end)),
    };
};

export interface SignPeriod {
    from: Date;
    to: Date;
}

/** The stretches of time (within the table) in which Chiron was in a sign; it can come and go on a retrograde. */
export const periodsIn = (sign: SignId): SignPeriod[] => {
    const out: SignPeriod[] = [];
    let open: number | null = null;
    // Four-day steps are fine enough: Chiron never crosses a sign and back inside that.
    for (let t = CHIRON_FIRST_MS; t <= CHIRON_LAST_MS; t += 4 * DAY) {
        const inside = signOf(chironLongitude(t)) === sign;
        if (inside && open === null) open = t;
        if (!inside && open !== null) {
            out.push({ from: new Date(open), to: new Date(t) });
            open = null;
        }
    }
    if (open !== null)
        out.push({ from: new Date(open), to: new Date(CHIRON_LAST_MS) });
    return out;
};

export const isSign = (value: string | undefined): value is SignId =>
    SIGNS.includes(value as SignId);
